import { Routes, Route, NavLink } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { api, type Lead, type Commission } from '../lib/api'
import { getProduct } from '../lib/products'
import { StatusChip } from './Dashboard'
import AdminFeedback from './AdminFeedback'

export default function Admin() {
  return (
    <section className="section py-10">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-white">Admin</h1>
        <p className="text-ink-500 mt-1">All referrers, all leads, all commissions.</p>
      </header>

      <nav className="flex gap-2 mb-8 flex-wrap">
        <Tab to="" label="Leads" end />
        <Tab to="referrers" label="Referrers" />
        <Tab to="payouts" label="Payouts" />
        <Tab to="feedback" label="Feedback" />
      </nav>

      <Routes>
        <Route index element={<AdminLeads />} />
        <Route path="referrers" element={<AdminReferrers />} />
        <Route path="payouts" element={<AdminPayouts />} />
        <Route path="feedback" element={<AdminFeedback />} />
      </Routes>
    </section>
  )
}

function Tab({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `px-4 py-2 rounded-lg text-sm font-medium ${
          isActive ? 'bg-brand-600 text-white' : 'bg-ink-900 border border-ink-800 text-ink-100 hover:border-ink-500'
        }`
      }
    >
      {label}
    </NavLink>
  )
}

function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Lead['status'] | 'all'>('all')

  useEffect(() => {
    api
      .get<{ leads: Lead[] }>('/api/admin/leads')
      .then((d) => setLeads(d.leads))
      .finally(() => setLoading(false))
  }, [])

  async function updateStatus(id: string, status: Lead['status']) {
    await api.patch(`/api/admin/leads/${id}`, { status })
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)))
  }

  const shown = filter === 'all' ? leads : leads.filter((l) => l.status === filter)

  return (
    <>
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'new', 'contacted', 'won', 'lost', 'paid'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`chip border capitalize ${
              filter === s ? 'bg-brand-600 border-brand-600 text-white' : 'bg-ink-900 border-ink-800 text-ink-100'
            }`}
          >
            {s} ({s === 'all' ? leads.length : leads.filter((l) => l.status === s).length})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-ink-500">Loading…</p>
      ) : shown.length === 0 ? (
        <div className="card text-center text-ink-500">No leads.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-ink-500 border-b border-ink-800">
              <tr>
                <th className="pb-2">Business</th>
                <th className="pb-2">Product</th>
                <th className="pb-2">Referrer</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-800">
              {shown.map((l) => {
                const p = getProduct(l.productSlug)
                return (
                  <tr key={l.id}>
                    <td className="py-3">
                      <div className="font-medium text-white">{l.businessName}</div>
                      <div className="text-xs text-ink-500">{l.ownerName} · {l.phone}</div>
                      <div className="text-xs text-ink-500">{l.city}, {l.country}</div>
                    </td>
                    <td className="py-3">{p?.icon} {p?.name} <span className="text-xs text-ink-500">({l.plan})</span></td>
                    <td className="py-3 font-mono text-xs">{l.referrerCode}</td>
                    <td className="py-3"><StatusChip status={l.status} /></td>
                    <td className="py-3">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value as Lead['status'])}
                        className="input py-1 text-xs"
                      >
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="won">won</option>
                        <option value="lost">lost</option>
                        <option value="paid">paid</option>
                      </select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

type AdminReferrer = {
  id: string
  email: string
  fullName: string
  referralCode: string
  status: 'active' | 'suspended'
  leadsTotal: number
  leadsWon: number
  commissionPaid: number
  createdAt: string
}

function AdminReferrers() {
  const [rows, setRows] = useState<AdminReferrer[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    api.get<{ referrers: AdminReferrer[] }>('/api/admin/referrers').then((d) => setRows(d.referrers)).finally(() => setLoading(false))
  }, [])
  async function toggle(id: string, current: 'active' | 'suspended') {
    const status = current === 'active' ? 'suspended' : 'active'
    await api.patch(`/api/admin/referrers/${id}`, { status })
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)))
  }
  return loading ? (
    <p className="text-ink-500">Loading…</p>
  ) : (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-ink-500 border-b border-ink-800">
          <tr>
            <th className="pb-2">Partner</th>
            <th className="pb-2">Code</th>
            <th className="pb-2">Leads</th>
            <th className="pb-2">Won</th>
            <th className="pb-2">Paid €</th>
            <th className="pb-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-800">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="py-3">
                <div className="font-medium text-white">{r.fullName}</div>
                <div className="text-xs text-ink-500">{r.email}</div>
              </td>
              <td className="py-3 font-mono text-xs">{r.referralCode}</td>
              <td className="py-3">{r.leadsTotal}</td>
              <td className="py-3">{r.leadsWon}</td>
              <td className="py-3 text-emerald-400">€{r.commissionPaid.toFixed(2)}</td>
              <td className="py-3">
                <button onClick={() => toggle(r.id, r.status)} className={`chip border ${r.status === 'active' ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400 border-red-500/30'}`}>
                  {r.status}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminPayouts() {
  const [rows, setRows] = useState<Commission[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'pending' | 'paid'>('pending')
  const [marking, setMarking] = useState<string | null>(null)

  const reload = useCallback(() => {
    setLoading(true)
    api.get<{ commissions: Commission[] }>(`/api/admin/commissions?status=${filter}`)
      .then((d) => setRows(d.commissions))
      .finally(() => setLoading(false))
  }, [filter])

  useEffect(() => { reload() }, [reload])

  const markPaid = async (id: string) => {
    setMarking(id)
    try {
      await api.patch(`/api/admin/commissions/${id}/paid`, {})
      reload()
    } finally {
      setMarking(null) }
  }

  const exportCsv = () => {
    const header = 'id,referrer,email,business,product,kind,month,amount,status,created_at'
    const lines = rows.map((c) =>
      [c.id, `"${c.referrerName}"`, c.referrerEmail, `"${c.businessName}"`, c.productSlug,
       c.kind, c.monthIndex ?? '', c.amount.toFixed(2), c.status, c.createdAt].join(','),
    )
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `commissions-${filter}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const total = rows.reduce((s, c) => s + c.amount, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {(['pending', 'paid'] as const).map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${filter === s ? 'bg-brand-600 text-white' : 'bg-ink-900 border border-ink-800 text-ink-100'}`}>
              {s}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} className="btn-secondary text-sm">Export CSV</button>
      </div>

      {loading ? (
        <p className="text-ink-500">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card text-center text-ink-500">No {filter} commissions.</div>
      ) : (
        <>
          <div className="card text-sm text-ink-500">
            {rows.length} item{rows.length !== 1 ? 's' : ''} · Total:
            <span className="ml-1 font-semibold text-emerald-400">€{total.toFixed(2)}</span>
          </div>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink-500 border-b border-ink-800">
                <tr>
                  <th className="pb-2">Referrer</th>
                  <th className="pb-2">Business</th>
                  <th className="pb-2">Type</th>
                  <th className="pb-2 text-right">Amount</th>
                  <th className="pb-2 text-right">Date</th>
                  {filter === 'pending' && <th className="pb-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-800">
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td className="py-3">
                      <div className="font-medium text-white">{c.referrerName}</div>
                      <div className="text-xs text-ink-500">{c.referrerEmail}</div>
                    </td>
                    <td className="py-3">
                      <div className="text-white">{c.businessName}</div>
                      <div className="text-xs text-ink-500">{c.productSlug}</div>
                    </td>
                    <td className="py-3 capitalize">
                      {c.kind}{c.monthIndex ? ` m${c.monthIndex}` : ''}
                    </td>
                    <td className="py-3 text-right font-semibold text-emerald-400">€{c.amount.toFixed(2)}</td>
                    <td className="py-3 text-right text-ink-500 text-xs">{c.createdAt.slice(0, 10)}</td>
                    {filter === 'pending' && (
                      <td className="py-3 text-right">
                        <button onClick={() => markPaid(c.id)} disabled={marking === c.id}
                          className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50">
                          {marking === c.id ? '…' : 'Mark paid'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
